import { CommonModule } from '@angular/common';
import { AfterViewChecked, Component, ElementRef, HostListener, inject, Input, Renderer2, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { TabGetService } from '@techextensor/tab-core-utility';
import { TabSdk } from '@techextensor/tab-sdk';
import { from, Subject, takeUntil } from 'rxjs';
import { Constants } from '../../../core/const/constants';
import { TranslationToken } from '../../../core/const/translation.token';
import { ContentPreviewPipe } from '../../../core/pipes/content-preview.pipe';

@Component({
    selector: 'app-global-search',
    imports: [CommonModule, FormsModule, ContentPreviewPipe],
    templateUrl: './global-search.component.html',
    styleUrls: ['./global-search.component.scss']
})
export class GlobalSearchComponent implements AfterViewChecked {
  constructor(private renderer: Renderer2) {
    this.searchTextBox = {} as ElementRef;
  }

  @Input() isDoctSearch: boolean = false;

  @ViewChild('globalSearch') globalSearch!: ElementRef;
  @ViewChild('searchTextBox') searchTextBox!: ElementRef;
  private sanitizer: any = inject(DomSanitizer); // Inject DomSanitizer
  public tabGetService: TabGetService = inject(TabGetService);
  public router: Router = inject(Router);
  private destroy$ = new Subject<void>();

  searchResults: any; // Define the searchResults property
  selectedGroupIndex: any = 0;
  // filteredResults:any; // Define the filteredResults property
  // groupedResults: GroupedResults = {}; // Define the groupedResults property
  // currentGroup: string | null = null; // Define the currentGroup property

  isShowGlobalSearch: boolean = false;
  isFullScreen: boolean = false;
  searchTerm: string = '';
  isLoading: boolean = false;

  ngOnInit(): void {
    this.router.events
    .pipe(takeUntil(this.destroy$))
    .subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.clearSearch();
        this.isFullScreen = false;
        this.isShowGlobalSearch = false;
        const body = document.body;
        if (body.classList.contains('search-activated')) {
          this.renderer.removeClass(body, 'search-activated'); // Remove class if it exists.
        }
      }
    });
  }

  /**
   * The ngOnDestroy() method is called when the component is about to be destroyed.
   * In this method, we complete the destroy$ observable to indicate that the component is no longer needed.
   */
  ngOnDestroy(): void {
    // Complete the destroy$ observable to indicate that the component is no longer needed
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewChecked(): void {
    if (this.searchTextBox && this.isShowGlobalSearch && !this.isFullScreen) {
      this.searchTextBox.nativeElement.focus(); // Safe to access nativeElement here
    }
  }
  /**
   * Toggles the visibility of the global search component.
   * Sets the search component to non-fullscreen mode.
   */
  toggleGlobalSearch(): void {
    this.isShowGlobalSearch = !this.isShowGlobalSearch;
    this.isFullScreen = false;
  }

  /**
   * Called when the user submits a search query.
   * If the search query is longer than 2 characters, calls the Search API and
   * updates the `searchResults`, `filteredResults`, and `groupedResults` properties.
   * If the search query is shorter than 2 characters, clears the search results.
   */
  onSearch(): void {
    if (this.searchTerm.length >= 3) {
      // Show the loading spinner
      this.isLoading = true;

      // Set limit based on isFullScreen
      const payload: any = {
        searchQuery: this.searchTerm,
        limit: this.isFullScreen ? 1000 : 5
      }

      if(this.isDoctSearch) {
        payload.appObjectId = Constants.TABD_Documents_Id
      }
      
      // Call the Search API with the appropriate limit
      from(TabSdk.crud.getSearchData(payload)).subscribe({
        next: (response: any) => {
          // Hide the loading spinner
          this.isLoading = false;

          // If the response contains results, process them and update the component state
          // New API uses camelCase 'result'
          const results = response?.Result ?? [];
          if (results?.length) {
            this.searchResults = this.processData(results);
          } else {
            // If the response does not contain results, clear the search results
            this.searchResults = [];
          }
        },
        error: (error: any) => {
          // Hide the loading spinner
          this.isLoading = false;

          // Log any errors that occurred while fetching search results
          console.error('Error fetching search results:', error);
        }
      });
    } else {
      // Clear the search results if the search query is shorter than 2 characters
      this.clearSearch();
    }
  }

  // /**
  //  * Groups the search results by AppObject (e.g. 'Task', 'Project', etc.).
  //  * @param results The search results to group.
  //  * @returns An object with AppObject names as keys and arrays of search results as values.
  //  */
  // groupSearchResults(results: any): GroupedResults {
  //   return results.reduce((acc: any, item: any) => {
  //     // If the AppObject is not already a key in the accumulator, add it
  //     if (!acc[item.AppObject]) {
  //       acc[item.AppObject] = [];
  //     }
  //     // Add the current search result to the array of results for the AppObject
  //     acc[item.AppObject].push(item);
  //     // Return the updated accumulator
  //     return acc;
  //   }, {} as GroupedResults);
  // }

  /**
   * Filters the search results by the given AppObject (e.g. 'Task', 'Project', etc.).
   * @param group The AppObject to filter the search results by.
   */
  filterByGroup(index: number): void {
    // Set the current group to the given AppObject
    // this.currentGroup = group;
    // Get the search results for the given AppObject
    // this.filteredResults = this.groupedResults[group] || [];
    this.selectedGroupIndex = index;
  }

  // /**
  //  * Gets all search results, without filtering by AppObject.
  //  */
  // getAllResults(): void {
  //   // Clear the current group, so that all search results are shown
  //   // this.currentGroup = null;
  //   // Set the filtered results to the complete list of search results
  //   // this.filteredResults = this.searchResults;
  // }

  /**
   * Clears the search results and resets the search form.
   */
  clearSearch(): void {
    this.searchTerm = '';
    this.searchResults = [];
    this.selectedGroupIndex = 0;
    // this.filteredResults = [];
    // this.groupedResults = {};
    // this.currentGroup = null;
    this.isLoading = false;
  }

  /**
   * Toggles the full-screen search results view.
   * Hides the compact search results view and shows the full-screen search results view.
   */
  viewAllResults(): void {
    this.searchResults = [];
    this.selectedGroupIndex = 0;
    this.isFullScreen = true;
    this.isShowGlobalSearch = false;

    const body = document.body;
    if (body.classList.contains('search-activated')) {
      this.renderer.removeClass(body, 'search-activated'); // Remove class if it exists.
    }
    this.onSearch();
  }

  /**
   * Closes the full-screen search results view.
   * Hides the full-screen search results view and shows the compact search results view.
   */
  closeFullScreen(): void {
    this.searchResults = [];
    this.selectedGroupIndex = 0;
    this.isFullScreen = false;
    this.isShowGlobalSearch = true;
    const body = document.body; // Access the body element directly.

    this.renderer.addClass(body, 'search-activated');

    this.onSearch();
  }

  /**
   * Handles clicks outside the global search component.
   * Closes the search component if it's visible and the click occurred outside of it.
   *
   * @param event - The MouseEvent triggered by the click.
   */
  @HostListener('document:mousedown', ['$event'])
  public onOutsideClick(event: any): void {
    // Check if the global search is visible, not in fullscreen, and the click is outside the search area
    if (
      this.isShowGlobalSearch &&
      !this.isFullScreen &&
      this.globalSearch?.nativeElement &&
      !this.globalSearch.nativeElement.contains(event.target)
    ) {
      // Close the global search and clear the search results
      this.closeGlobalSearch();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  /**
   * Handles the Escape key being pressed.
   * Closes the global search if it's visible.
   *
   * @param event - The KeyboardEvent triggered by the Escape key.
   */
  onEscPress(event: any): void {
    // Check if the Escape key was pressed
    if (event.key === 'Escape') {
      // Close the global search
      this.closeGlobalSearch();
    }
  }

  /**
   * Processes the search results by mapping over the array and extracting the AppObject and SearchInfo from each item.
   * @param searchResults - The array of search results from the API.
   * @returns An array of objects with AppObject and SearchInfo properties.
   */
  processData(searchResults: any): any[] {
    return searchResults.map((item: any) => {
      const appObjectDetails = TabSdk.metadataUtils.getAppObjectByIdOrName(item.AppObjectId);
      item.AppObject = appObjectDetails?.SystemDBTableName;
      return item;
      // /**
      //  * Extract the AppObject and SearchInfo from the item and return a new object with those properties.
      //  */
      // return {
      //   AppObject: appObjectDetails.SystemDBTableName,
      //   SearchInfo: item.GlobalSearchInfo,
      // };
    });
  }

  /**
   * Opens a link in a new tab based on the provided RedirectUrl.
   * The link is constructed by prepending the base URL and appending the RedirectUrl.
   * If the RedirectUrl is empty, the link is not opened and the search results are not cleared.
   * @param RedirectUrl - The URL to be opened in the new tab.
   */
  openLink(item: any): void {
    let url;    
    if(item.SearchNavigationType === 'screen' && item?.RedirectUrl?.length) {
      url = `${window.location.origin}/${TabSdk.store.appName}/screen/${item.RedirectUrl}`; 
    } else if(item.SearchNavigationType === 'url' && item?.RedirectUrl?.length) {
     url = `${window.location.origin}/${item.RedirectUrl}`;
    }

    if (url) {
      window.open(url, '_blank');
      // Close the search results and clear the search field
      // this.isShowGlobalSearch = false;
      // this.isFullScreen = false;
      // this.clearSearch();
    } else {
      TabSdk.ui.showError({ message: TranslationToken.InvalidRedirectUrl });
    }
  }

  getSafeHtml(content: string) {
    return this.sanitizer.bypassSecurityTrustHtml(content);
  }
  
  /**
   * Closes the global search component and clears the search results.
   * This method is called when the user clicks outside of the search component or presses the escape key.
   */
  closeGlobalSearch(): void {
    // Hide the global search
    this.isShowGlobalSearch = false;

    // Remove the CSS class from the body indicating that the search is activated
    const body = document.body;
    if (body.classList.contains('search-activated')) {
      this.renderer.removeClass(body, 'search-activated');
    }

    // Clear the search results and reset the search form
    this.clearSearch();
  }

}
